<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { MarIconBtn, MarSidebarItem } from '@nimling/components-nimtech'
import type { Page } from '@/types'

defineOptions({ name: 'PageTreeItem' })

const props = withDefaults(defineProps<{ page: Page; tree: Map<string, Page[]>; depth?: number; edit?: boolean }>(), { depth: 0, edit: false })
const emit = defineEmits<{ create: [parent: Page] }>()

const route = useRoute()
const router = useRouter()

const path = computed(() => `/pages/${props.page.id}`)
const children = computed(() => props.tree.get(props.page.id) ?? [])

const holdsActive = (page: Page): boolean =>
  route.path === `/pages/${page.id}` || (props.tree.get(page.id) ?? []).some(holdsActive)

const expanded = computed(() => children.value.some(holdsActive))

const onClick = (event: MouseEvent) => {
  event.preventDefault()
  if (!props.edit) void router.push(path.value)
}
</script>

<template>
  <MarSidebarItem
    :id="page.id"
    :label="page.name"
    :icon="page.icon ?? 'dashboard'"
    :href="edit ? undefined : path"
    :active="!edit && route.path === path"
    :depth="depth"
    :default-expanded="edit || expanded"
    @click="onClick"
  >
    <template v-if="!edit" #actions>
      <MarIconBtn icon="plus" tooltip="Create sub page" @click.stop="emit('create', page)" />
    </template>
    <template v-if="children.length" #default>
      <PageTreeItem
        v-for="child in children"
        :key="child.id"
        :page="child"
        :tree="tree"
        :depth="depth + 1"
        :edit="edit"
        @create="emit('create', $event)"
      />
    </template>
  </MarSidebarItem>
</template>
